export type OrderDatePreset = 'ALL' | 'TODAY' | 'YESTERDAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'THIS_YEAR' | 'CUSTOM'

export interface OrderFilters {
  keyword: string
  salePreset: OrderDatePreset
  saleFrom: string
  saleTo: string
  intakePreset: OrderDatePreset
  intakeFrom: string
  intakeTo: string
  customerId: string
  salesUserIds: string[]
  collectorUserIds: string[]
  storeIds: string[]
  sourceKinds: string[]
  sourceCustomerIds: string[]
  partnerFunderCustomerIds: string[]
}

export const ORDER_COLUMN_KEYS = [
  'orderNo', 'customer', 'cover', 'productName', 'brandSeries', 'codes', 'condition', 'engraving',
  'accessories', 'cost', 'compositeCost', 'sale', 'profit', 'recyclers', 'stockedAt', 'primarySales',
  'store', 'orderedAt', 'createdBy', 'note', 'shipping', 'payment',
] as const

export type OrderColumnKey = typeof ORDER_COLUMN_KEYS[number]
export type OrderDensity = 'compact' | 'normal' | 'comfortable'
export interface OrderTablePreference {
  version: 2
  columns: OrderColumnKey[]
  pinned: OrderColumnKey[]
  density: OrderDensity
}

export const DEFAULT_ORDER_COLUMNS: OrderColumnKey[] = ORDER_COLUMN_KEYS.slice(0, 20)
export const DEFAULT_ORDER_PINNED: OrderColumnKey[] = ['orderNo', 'customer']
export const ORDER_COST_COLUMNS: OrderColumnKey[] = ['cost', 'compositeCost', 'profit']

function shanghaiToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now)
}

function formatDate(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}

export function rangeForPreset(preset: OrderDatePreset, today = shanghaiToday()): [string, string] {
  if (preset === 'ALL' || preset === 'CUSTOM') return ['', '']
  const date = new Date(`${today}T00:00:00Z`)
  if (preset === 'TODAY') return [today, today]
  if (preset === 'YESTERDAY') {
    date.setUTCDate(date.getUTCDate() - 1)
    const value = formatDate(date)
    return [value, value]
  }
  if (preset === 'THIS_WEEK') {
    const day = date.getUTCDay() || 7
    const start = new Date(date); start.setUTCDate(start.getUTCDate() - day + 1)
    return [formatDate(start), today]
  }
  if (preset === 'THIS_MONTH') return [`${today.slice(0, 7)}-01`, today]
  return [`${today.slice(0, 4)}-01-01`, today]
}

export function defaultOrderFilters(today?: string): OrderFilters {
  const [saleFrom, saleTo] = rangeForPreset('THIS_MONTH', today)
  return {
    keyword: '', salePreset: 'THIS_MONTH', saleFrom, saleTo,
    intakePreset: 'ALL', intakeFrom: '', intakeTo: '', customerId: '',
    salesUserIds: [], collectorUserIds: [], storeIds: [], sourceKinds: [],
    sourceCustomerIds: [], partnerFunderCustomerIds: [],
  }
}

export function emptyOrderFilters(): OrderFilters {
  return {
    keyword: '', salePreset: 'ALL', saleFrom: '', saleTo: '', intakePreset: 'ALL', intakeFrom: '',
    intakeTo: '', customerId: '', salesUserIds: [], collectorUserIds: [], storeIds: [], sourceKinds: [],
    sourceCustomerIds: [], partnerFunderCustomerIds: [],
  }
}

export function setDatePreset(filters: OrderFilters, kind: 'sale' | 'intake', preset: OrderDatePreset, today?: string) {
  const next = structuredClone(filters)
  const [from, to] = rangeForPreset(preset, today)
  if (kind === 'sale') { next.salePreset = preset; next.saleFrom = from; next.saleTo = to }
  else { next.intakePreset = preset; next.intakeFrom = from; next.intakeTo = to }
  return next
}

export function clearOrderFilterGroup(filters: OrderFilters, keys: Array<keyof OrderFilters>) {
  const next = structuredClone(filters)
  for (const key of keys) {
    const value = next[key]
    ;(next as unknown as Record<string, unknown>)[key] = Array.isArray(value) ? [] : ''
  }
  return next
}

export function serializeOrderFilters(filters: OrderFilters, pageNo = 1, pageSize = 100,
  sortField = 'saleDate', sortOrder = 'desc') {
  const params = new URLSearchParams({ pageNo: String(pageNo), pageSize: String(pageSize), tab: 'OPEN', sortField, sortOrder })
  const append = (key: string, value: string) => { if (value.trim()) params.set(key, value.trim()) }
  const appendMany = (key: string, values: string[]) => values.forEach((value) => params.append(key, value))
  append('keyword', filters.keyword)
  append('saleFrom', filters.saleFrom); append('saleTo', filters.saleTo)
  append('intakeFrom', filters.intakeFrom); append('intakeTo', filters.intakeTo)
  append('customerId', filters.customerId)
  appendMany('salesUserIds', filters.salesUserIds); appendMany('collectorUserIds', filters.collectorUserIds)
  appendMany('storeIds', filters.storeIds); appendMany('sourceKinds', filters.sourceKinds)
  appendMany('sourceCustomerIds', filters.sourceCustomerIds)
  appendMany('partnerFunderCustomerIds', filters.partnerFunderCustomerIds)
  return params
}

export function normalizeOrderTablePreference(value: unknown, canViewCost: boolean): OrderTablePreference {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const allowed = new Set<OrderColumnKey>(ORDER_COLUMN_KEYS.filter((key) => canViewCost || !ORDER_COST_COLUMNS.includes(key)))
  const source = Array.isArray(record.columns) ? record.columns : DEFAULT_ORDER_COLUMNS
  const columns = [...new Set(source.filter((key): key is OrderColumnKey => typeof key === 'string' && allowed.has(key as OrderColumnKey)))]
  const fallback = DEFAULT_ORDER_COLUMNS.filter((key) => allowed.has(key))
  const visible = columns.length ? columns : fallback
  const pinnedSource = Array.isArray(record.pinned) ? record.pinned : DEFAULT_ORDER_PINNED
  const pinned = [...new Set(pinnedSource.filter((key): key is OrderColumnKey =>
    typeof key === 'string' && visible.includes(key as OrderColumnKey)))].slice(0, 2)
  const density = record.density === 'normal' || record.density === 'comfortable' ? record.density : 'compact'
  return { version: 2, columns: visible, pinned, density }
}

export function orderOrderColumns(columns: OrderColumnKey[], pinned: OrderColumnKey[]) {
  return [...pinned.filter((key) => columns.includes(key)), ...columns.filter((key) => !pinned.includes(key))]
}

export function moveOrderColumn(columns: OrderColumnKey[], key: OrderColumnKey, direction: -1 | 1) {
  const from = columns.indexOf(key); const to = from + direction
  if (from < 0 || to < 0 || to >= columns.length) return columns
  const next = [...columns]; [next[from], next[to]] = [next[to]!, next[from]!]
  return next
}
